// Daily biome theme — both a course title and a visual palette, derived
// from the date. 5 biomes rotate. Each supplies color tokens for the
// nebula/star/planet/ball shaders so every course day looks different.

import { mulberry32, seedFromString } from "./rng.js";

export type BiomeId =
  | "starlight"
  | "aurora"
  | "celestial"
  | "crystal"
  | "golden";

export interface BiomeTheme {
  id: BiomeId;
  name: string; // Short nickname ("Starlight Fields")
  fullName: string; // Marketing ("Starlight Fields by Cosmic Crew")
  tagline: string;
  // Nebula shader palette.
  nebulaA: [number, number, number];
  nebulaB: [number, number, number];
  nebulaC: [number, number, number];
  nebulaD: [number, number, number];
  // Star palette (hex strings — shader reads them as colors).
  starPalette: string[];
  dustPalette: [string, string, string]; // warm, cool, hot
  // Accent for trails / UI hits.
  accent: string;
  // Ball body glow.
  ballGlow: string;
  // Planet hue range (HSL hue 0..1 start + span).
  planetHueStart: number;
  planetHueSpan: number;
  // Tone mapping exposure.
  exposure: number;
  // Virtual sun (world-space direction, unnormalized is fine). Planet
  // shaders Lambert-shade from this; god-rays post-pass projects it.
  sunDir: [number, number, number];
  // Sun tint for god rays.
  sunTint: string;
  // Adjectives for auto-generated course name suffix.
  descriptorPool: string[];
}

const THEMES: Record<BiomeId, BiomeTheme> = {
  starlight: {
    id: "starlight",
    name: "Starlight Fields",
    fullName: "Starlight Fields",
    tagline: "Clear night with shimmering stars",
    nebulaA: [0.10, 0.05, 0.25],
    nebulaB: [0.04, 0.16, 0.32],
    nebulaC: [0.22, 0.06, 0.33],
    nebulaD: [0.015, 0.015, 0.04],
    starPalette: ["#ffffff", "#cfe2ff", "#ffe3b0", "#f0ccff"],
    dustPalette: ["#7a6cd4", "#3b7ac4", "#d89aff"],
    accent: "#7cf5ff",
    ballGlow: "#7cf5ff",
    planetHueStart: 0.55,
    planetHueSpan: 0.18,
    exposure: 1.1,
    sunDir: [0.6, 0.4, -0.2],
    sunTint: "#ffe3b0",
    descriptorPool: [
      "Expanse",
      "Veil",
      "Threshold",
      "Silhouette",
      "Quiet",
      "Fields",
    ],
  },
  aurora: {
    id: "aurora",
    name: "Aurora Seas",
    fullName: "Aurora Seas",
    tagline: "Colorful nebulae and cosmic lights",
    nebulaA: [0.05, 0.25, 0.22],
    nebulaB: [0.12, 0.05, 0.40],
    nebulaC: [0.40, 0.10, 0.55],
    nebulaD: [0.02, 0.04, 0.06],
    starPalette: ["#ffffff", "#aaffdd", "#ffafd0", "#c8a6ff"],
    dustPalette: ["#6adbc1", "#8b7cff", "#ff9ed8"],
    accent: "#a2ffda",
    ballGlow: "#a2ffda",
    planetHueStart: 0.35,
    planetHueSpan: 0.25,
    exposure: 1.15,
    sunDir: [0.3, 0.5, 0.4],
    sunTint: "#c8ffe0",
    descriptorPool: [
      "Current",
      "Drift",
      "Ripple",
      "Curtain",
      "Shore",
      "Tides",
    ],
  },
  celestial: {
    id: "celestial",
    name: "Celestial Clouds",
    fullName: "Celestial Clouds",
    tagline: "Bright skies and soft cosmics",
    nebulaA: [0.35, 0.20, 0.55],
    nebulaB: [0.42, 0.18, 0.48],
    nebulaC: [0.9, 0.75, 0.9],
    nebulaD: [0.05, 0.05, 0.15],
    starPalette: ["#ffffff", "#ffe6f2", "#e9d4ff", "#fff6cc"],
    dustPalette: ["#ffcfe8", "#b39cff", "#ff9cb4"],
    accent: "#ff9ed8",
    ballGlow: "#fff0a6",
    planetHueStart: 0.0,
    planetHueSpan: 0.18,
    exposure: 1.3,
    sunDir: [0.5, 0.6, 0.2],
    sunTint: "#ffeecc",
    descriptorPool: [
      "Meadows",
      "Heights",
      "Archipelago",
      "Reaches",
      "Bloom",
      "Glade",
    ],
  },
  crystal: {
    id: "crystal",
    name: "Crystal Floats",
    fullName: "Crystal Floats",
    tagline: "Crystal fragments and icy tones",
    nebulaA: [0.10, 0.14, 0.35],
    nebulaB: [0.10, 0.32, 0.45],
    nebulaC: [0.25, 0.40, 0.55],
    nebulaD: [0.01, 0.03, 0.06],
    starPalette: ["#ffffff", "#bcf0ff", "#e0f3ff", "#aaddff"],
    dustPalette: ["#7aa6d4", "#a8c7ff", "#c9e8ff"],
    accent: "#8dd8ff",
    ballGlow: "#c8f2ff",
    planetHueStart: 0.52,
    planetHueSpan: 0.1,
    exposure: 1.1,
    sunDir: [0.4, 0.6, -0.3],
    sunTint: "#dfeaff",
    descriptorPool: [
      "Shard",
      "Prism",
      "Quench",
      "Lattice",
      "Refraction",
      "Halo",
    ],
  },
  golden: {
    id: "golden",
    name: "Golden Dawn",
    fullName: "Golden Dawn",
    tagline: "Warm light and peaceful orbit",
    nebulaA: [0.35, 0.18, 0.08],
    nebulaB: [0.55, 0.30, 0.12],
    nebulaC: [0.85, 0.55, 0.25],
    nebulaD: [0.05, 0.025, 0.01],
    starPalette: ["#ffffff", "#ffe6b0", "#ffcf8a", "#ffd6cc"],
    dustPalette: ["#d4a36c", "#ff9e6b", "#ffd28a"],
    accent: "#ffd27c",
    ballGlow: "#ffd68a",
    planetHueStart: 0.05,
    planetHueSpan: 0.1,
    exposure: 1.25,
    sunDir: [0.7, 0.35, 0.15],
    sunTint: "#ffc98a",
    descriptorPool: [
      "Horizon",
      "Dawn",
      "Warmth",
      "Crest",
      "Ember",
      "Glow",
    ],
  },
};

const ORDER: BiomeId[] = ["starlight", "aurora", "celestial", "crystal", "golden"];

export function biomeForDate(date: string): BiomeTheme {
  // Stable pick — hash the date, mod 5.
  const h = seedFromString(`biome/v1/${date}`);
  const id = ORDER[Math.abs(h) % ORDER.length]!;
  return THEMES[id];
}

// Hole-level biome: each hole in a day gets its own palette rotated off
// the day's anchor biome. Threaded through `biome/v1/${date}` so the
// anchor is stable, then offset by hole index so H1..H9 step through
// five biomes. Two days in a row never start with the same sequence,
// and the 9 holes always show at least 4 distinct palettes.
export function biomeForHole(date: string, holeIndex: number): BiomeTheme {
  const h = seedFromString(`biome/v1/${date}`);
  const start = Math.abs(h) % ORDER.length;
  // Phase per hole: step by 1 each time, but occasionally double-step
  // on a deterministic seed so adjacent holes aren't always the same
  // neighbor in the ORDER list.
  const jitterSeed = Math.abs(seedFromString(`hole-biome/v1/${date}/${holeIndex}`));
  const step = (holeIndex + (jitterSeed % 3)) % ORDER.length;
  const id = ORDER[(start + step) % ORDER.length]!;
  return THEMES[id];
}

// Procedural course name: biome + random descriptor noun, seeded by date
// so every day the public sees the same name. Reads like
// "Starlight Fields — Quiet Expanse".
export function courseNameForDate(date: string): string {
  const theme = biomeForDate(date);
  const rng = mulberry32(seedFromString(`coursename/v1/${date}`));
  const noun =
    theme.descriptorPool[Math.floor(rng() * theme.descriptorPool.length)]!;
  // One of a few connecting words.
  const connectors = ["—", "·", "of the"];
  const connector = connectors[Math.floor(rng() * connectors.length)]!;
  if (connector === "of the") {
    return `${noun} of the ${theme.name}`;
  }
  return `${theme.name} ${connector} ${noun}`;
}
